import { Button, Divider, useMediaQuery, useTheme } from "@mui/material";
import React, { useState } from "react";
import { themes } from "../../ColorFont/themes";
import { Box, Typography, Paper } from "@mui/material";
import { fonts, colors, size } from "../../ColorFont/color";
import { url } from "../../ConstUrl/Url";
import { fontThemes } from "../../ColorFont/fontTheme";
import { useTranslation } from "react-i18next";

const Settings = ({ isUserID }) => {
  const { t } = useTranslation("settings");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeTab, setActiveTab] = useState('colors'); // 'colors' or 'fonts'
  
  const colorThemeArray = [
    {
      theme: "default",
      colorSet: ["#462521", "#c68d86", "#e6cecb", "#868889", "#fcfafa"],
      name: "Default",
      description: t("colorDesc1")
    },
    {
      theme: "classic",
      colorSet: ["#49A74C", "#AEDC81", "#EBFFD7", "#868889", "#fcfafa"],
      name: t("color1"),
      description: t("colorDesc1")
    },
    {
      theme: "modern",
      colorSet: ["#4682B4", "#6495ED", "#87CEFA", "#868889", "#fcfafa"],
      name: t("color2"),
      description: t("colorDesc2")
    },
    {
      theme: "tangerine",
      colorSet: ["#FF8C00", "#FFA07A", "#FFC080", "#868889", "#fcfafa"],
      name: t("color3"),
      description: t("colorDesc3")
    },
    {
      theme: "bubblegum",
      colorSet: ["#814b56", "#b27883", "#e5a7b2", "#868889", "#fcfafa"],
      name: t("color4"),
      description: t("colorDesc4")
    },
    {
      theme: "woodland",
      colorSet: ["#726e18", "#a39c46", "#d6cd74", "#868889", "#fcfafa"],
      name: t("color5"),
      description: t("colorDesc5")
    },
  ];

  const userId = isUserID;

  const [tempSelectedTheme, setTempSelectedTheme] = useState(() => {
    return localStorage.getItem("theme") || "classic";
  });

  const [tempSelectedFontTheme, setTempSelectedFontTheme] = useState(() => {
    return localStorage.getItem("fontTheme") || "classic";
  });

  const getPrimaryColor = () => {
    const selectedThemeData = colorThemeArray.find(
      (theme) => theme.theme === tempSelectedTheme
    );
    return selectedThemeData ? selectedThemeData.colorSet[0] : "#462521";
  };

  const handleApplyChanges = () => {
    localStorage.setItem("theme", tempSelectedTheme);
    localStorage.setItem("fontTheme", tempSelectedFontTheme);

    fetch(`${url}/api/users/${userId}/theme`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        theme: tempSelectedTheme,
        fontTheme: tempSelectedFontTheme,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log("Theme updated", data);
        window.location.reload();
      })
      .catch((error) => {
        console.error("Error updating theme", error);
        window.location.reload();
      });
  };

  const selectedThemeData = colorThemeArray.find(theme => theme.theme === tempSelectedTheme);
  const selectedFontTheme = fontThemes[tempSelectedFontTheme];

  return (
    <Box sx={{
      width: '100%',
      p: 0,
      m: 0,
    }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={{
          fontFamily: fonts.manrope || fonts.poppins,
          fontWeight: 800,
          fontSize: { xs: '24px', md: '30px' },
          color: colors.text_dark || '#171C17',
          letterSpacing: '-0.5px',
          mb: '4px',
        }}>
          {t("personalize")}
        </Typography>
        <Typography sx={{
          fontFamily: fonts.publicSans || fonts.poppins,
          fontSize: '14px',
          color: colors.text || '#919EAB',
        }}>
          {t("desc")}
        </Typography>
      </Box>

      {/* Navigation Tabs */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        mb: 4,
        borderBottom: `1px solid ${colors.border}`,
        position: 'relative'
      }}>
        <Box sx={{
          display: 'flex',
          gap: 2
        }}>
          <Button 
            onClick={() => setActiveTab('colors')}
            sx={{
              pb: 2,
              px: 3,
              fontWeight: 600,
              color: activeTab === 'colors' ? getPrimaryColor() : "gray",
              borderBottom: activeTab === 'colors' ? `3px solid ${getPrimaryColor()}` : 'none',
              '&:hover': {
                backgroundColor: 'transparent'
              }
            }}
          >
            {t("theme")}
          </Button>
          <Button 
            onClick={() => setActiveTab('fonts')}
            sx={{
              pb: 2,
              px: 3,
              fontWeight: 600,
              color: activeTab === 'fonts' ? getPrimaryColor() : "gray",
              borderBottom: activeTab === 'fonts' ? `3px solid ${getPrimaryColor()}` : 'none',
              '&:hover': {
                backgroundColor: 'transparent'
              }
            }}
          >
            {t("font")}
          </Button>
        </Box>
      </Box>

      {/* Color Themes Section */}
      {activeTab === 'colors' && (
        <Box sx={{
          mb: 6,
          animation: 'fadeIn 0.5s ease'
        }}>
          <Typography variant="h5" sx={{
            fontFamily: fonts.poppins,
            fontWeight: 600,
            mb: 3,
            color: colors.textPrimary,
            textAlign: 'center'
          }}>
            {t("select")}
          </Typography>
          
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 3,
            mb: 4
          }}>
            {colorThemeArray.map(({ theme, colorSet, name, description }) => (
              <Paper 
                key={theme}
                elevation={tempSelectedTheme === theme ? 6 : 2}
                onClick={() => setTempSelectedTheme(theme)}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  cursor: 'pointer',
                  border: tempSelectedTheme === theme ? `2px solid ${getPrimaryColor()}` : `1px solid ${colors.border}`,
                  transition: 'all 0.3s ease',
                  backgroundColor: colors.background,
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: `0 10px 20px rgba(0,0,0,0.1)`
                  },
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {tempSelectedTheme === theme && (
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    backgroundColor: getPrimaryColor(),
                    color: 'white',
                    px: 2,
                    py: 1,
                    borderBottomLeftRadius: 12,
                    fontSize: '0.8rem',
                    fontWeight: 600
                  }}>
                  {t("selected")}
                  </Box>
                )}
                
                <Typography variant="h6" sx={{
                  fontFamily: fonts.poppins,
                  fontWeight: 600,
                  mb: 1,
                  color: colors.textPrimary
                }}>
                  {name}
                </Typography>
                
                <Typography sx={{
                  fontFamily: fonts.poppins,
                  color: colors.textSecondary,
                  mb: 2,
                  fontSize: '0.9rem'
                }}>
                  {description}
                </Typography>
                
                <Box sx={{
                  display: 'flex',
                  height: 40,
                  borderRadius: 20,
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  mb: 2
                }}>
                  {colorSet.map((color, index) => (
                    <Box 
                      key={index}
                      sx={{
                        flex: 1,
                        backgroundColor: color,
                        position: 'relative',
                        '&:after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: 4,
                          backgroundColor: 'rgba(0,0,0,0.1)'
                        }
                      }}
                    />
                  ))}
                </Box>
                
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Typography sx={{
                    fontFamily: fonts.poppins,
                    fontSize: '0.8rem',
                    color: colors.textSecondary
                  }}>
                    {colorSet[0].toUpperCase()}
                  </Typography>
                  
                  <Box sx={{
                    display: 'flex',
                    gap: 1
                  }}>
                    {colorSet.slice(1).map((color, index) => (
                      <Box 
                        key={index}
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          backgroundColor: color,
                          border: `1px solid ${colors.border}`
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>
      )}

      {/* Font Styles Section */}
      {activeTab === 'fonts' && (
        <Box sx={{
          mb: 6,
          animation: 'fadeIn 0.5s ease'
        }}>
          <Typography variant="h5" sx={{
            fontFamily: fonts.poppins,
            fontWeight: 600,
            mb: 3,
            color: colors.textPrimary,
            textAlign: 'center'
          }}>
            {t("selectFont")}
          </Typography>
          
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: 3,
            mb: 4
          }}>
            {Object.entries(fontThemes).map(([themeKey, { poppins }]) => (
              <Paper
                key={themeKey}
                elevation={tempSelectedFontTheme === themeKey ? 4 : 1}
                onClick={() => setTempSelectedFontTheme(themeKey)}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  cursor: 'pointer',
                  border: tempSelectedFontTheme === themeKey ? `2px solid ${getPrimaryColor()}` : `1px solid ${colors.border}`,
                  transition: 'all 0.3s ease',
                  backgroundColor: colors.background,
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: `0 5px 15px rgba(0,0,0,0.1)`
                  }
                }}
              >
                <Typography variant="h6" sx={{
                  fontFamily: poppins,
                  fontWeight: 600,
                  mb: 1,
                  color: colors.textPrimary,
                  textTransform: 'capitalize'
                }}>
                  {themeKey} Style
                </Typography>
                
                <Typography sx={{
                  fontFamily: poppins,
                  color: colors.textSecondary,
                  fontSize: '0.9rem'
                }}>
                  {t("fontDesc1")}
                </Typography>
                
                {tempSelectedFontTheme === themeKey && (
                  <Box sx={{
                    mt: 2,
                    textAlign: 'right'
                  }}>
                    <Box sx={{
                      display: 'inline-block',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 12,
                      backgroundColor: getPrimaryColor(),
                      color: 'white',
                      fontSize: '0.7rem',
                      fontWeight: 600
                    }}>
                      {t("active")}
                    </Box>
                  </Box>
                )}
              </Paper>
            ))}
          </Box>
        </Box>
      )}

      {/* Preview Section */}
      <Paper elevation={4} sx={{
        p: isMobile ? 3 : 4,
        borderRadius: 3,
        mb: 4,
        backgroundColor: selectedThemeData?.colorSet[4] || '#fcfafa'
      }}>
        <Typography variant="h5" sx={{
          fontFamily: selectedFontTheme.poppins,
          fontWeight: 700,
          mb: 2,
          color: selectedThemeData?.colorSet[0] || '#462521',
          textAlign: 'center'
        }}>
          {t("themePreview")}
        </Typography>
        
        <Box sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: 4,
          mb: 3
        }}>
          <Box sx={{
            flex: 1,
            p: 3,
            borderRadius: 2,
            backgroundColor: selectedThemeData?.colorSet[2] || '#EBFFD7',
            borderLeft: `6px solid ${selectedThemeData?.colorSet[0] || '#462521'}`
          }}>
            <Typography sx={{
              fontFamily: selectedFontTheme.poppins,
              fontWeight: 600,
              mb: 1,
              color: colors.textPrimary
            }}>
              {t("primaryColor")}
            </Typography>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 2
            }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: selectedThemeData?.colorSet[0] || '#462521',
                border: `2px solid white`,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}/>
              <Typography sx={{
                fontFamily: selectedFontTheme.poppins,
                color: colors.textPrimary
              }}>
                {selectedThemeData?.name || 'Classic Theme'}
              </Typography>
            </Box>
            
            <Typography sx={{
              fontFamily: selectedFontTheme.poppins,
              fontWeight: 600,
              mb: 1,
              color: colors.textPrimary
            }}>
              {t("fontStyle")}
            </Typography>
            <Typography sx={{
              fontFamily: selectedFontTheme.poppins,
              color: colors.textPrimary,
              fontStyle: 'italic'
            }}>
              {tempSelectedFontTheme.charAt(0).toUpperCase() + tempSelectedFontTheme.slice(1)}
            </Typography>
          </Box>
          
          <Box sx={{
            flex: 2,
            p: 3,
            borderRadius: 2,
            backgroundColor: 'white',
            border: `1px solid ${colors.border}`
          }}>
            <Typography sx={{
              fontFamily: selectedFontTheme.poppins,
              mb: 2,
              color: colors.textPrimary,
              lineHeight: 1.6
            }}>
              {t("exampleDesc")} <strong>{selectedThemeData?.name || 'Classic'}</strong> {t("exampleDesc2")} <strong>{tempSelectedFontTheme}</strong> {t("exampleDesc3")}
            </Typography>
            
            <Button variant="contained" sx={{
              backgroundColor: selectedThemeData?.colorSet[0] || '#462521',
              color: 'white',
              fontFamily: selectedFontTheme.poppins,
              fontWeight: 600,
              py: 1.5,
              px: 3,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: selectedThemeData?.colorSet[1] || '#AEDC81'
              }
            }}>
              {t("exampleButton")}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Apply Button */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'center'
      }}>
        <Button
          variant="contained"
          onClick={handleApplyChanges}
          sx={{
            backgroundColor: getPrimaryColor(),
            color: 'white',
            fontFamily: fonts.poppins,
            fontWeight: 600,
            py: 2,
            px: 6,
            borderRadius: 3,
            fontSize: '1.1rem',
            textTransform: 'none',
            boxShadow: `0 4px 12px ${getPrimaryColor()}40`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 6px 16px ${getPrimaryColor()}60`,
              backgroundColor: getPrimaryColor()
            }
          }}
        >
          {t("applyChanges")}
        </Button>
      </Box>
    </Box>
  );
};

export default Settings;